import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { CodeSnippet } from '../../components/CodeSnippet/CodeSnippet';
import {
  PLUGIN_URL,
  colorPresetSnippet,
  constructorOptions,
  constructorSnippet,
  definitionFields,
  eventReference,
  eventSnippet,
  methodCount,
  methodGroups,
  playableExamples,
  scriptRecipes,
  setupScripts,
  viewShapeFields,
} from '../../content/achievementDocs';
import './App.css';

const SCRIPT_TAG = 'data-achievement-system-plugin';
const DEFAULT_ROUTE_PATH = '/home';
let pluginLoadPromise = null;

const DEMO_DEFINITIONS = [
  {
    id: 'demo-common',
    title: 'Demo Winner',
    description: 'Trigger a common unlock pop.',
    score: 15,
    category: 'demo',
  },
  {
    id: 'demo-marathon',
    title: 'Marathon (100 Steps)',
    description: 'Progress-driven unlock example.',
    score: 40,
    maxProgress: 100,
    category: 'demo',
  },
  {
    id: 'demo-rare',
    title: 'Rare Discovery',
    description: 'Trigger a rare notification variant.',
    score: 100,
    rare: true,
    category: 'demo',
  },
];

const LANDING_THEME_OPTIONS = [
  { value: 'xboxGreen', label: 'Xbox Green' },
  { value: 'brightGreen', label: 'Bright Green' },
  { value: 'blueGreen', label: 'Blue Green' },
  { value: 'brightBlue', label: 'Bright Blue' },
  { value: 'purple', label: 'Purple' },
  { value: 'red', label: 'Red' },
  { value: 'gold', label: 'Gold' },
];

const LANDING_POSITION_OPTIONS = [
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'top-right', label: 'Top Right' },
];

const makeId = (value) =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const normalizePath = (pathname) => {
  if (!pathname || pathname === '/') return DEFAULT_ROUTE_PATH;
  const trimmed = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed || DEFAULT_ROUTE_PATH;
};

const loadAchievementPlugin = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Browser window is unavailable.'));
  }

  if (window.AchievementSystem) {
    return Promise.resolve(window.AchievementSystem);
  }

  if (pluginLoadPromise) {
    return pluginLoadPromise;
  }

  pluginLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[${SCRIPT_TAG}="v2"]`);

    if (existing) {
      existing.addEventListener(
        'load',
        () => {
          if (window.AchievementSystem) {
            resolve(window.AchievementSystem);
            return;
          }
          reject(new Error('Plugin loaded but window.AchievementSystem is missing.'));
        },
        { once: true },
      );

      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load achievement plugin script.')),
        { once: true },
      );

      return;
    }

    const script = document.createElement('script');
    script.type = 'module';
    script.async = true;
    script.src = PLUGIN_URL;
    script.setAttribute(SCRIPT_TAG, 'v2');

    script.onload = () => {
      if (!window.AchievementSystem) {
        reject(new Error('Plugin loaded but window.AchievementSystem is missing.'));
        return;
      }
      resolve(window.AchievementSystem);
    };

    script.onerror = () => {
      reject(new Error('Failed to load achievement plugin script.'));
    };

    document.head.appendChild(script);
  });

  return pluginLoadPromise;
};

const DataTable = ({ columns, rows }) => (
  <div className="table-wrap">
    <table>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.key}>{column.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id || row.name}>
            {columns.map((column) => (
              <td key={`${row.id || row.name}-${column.key}`}>{row[column.key]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SidebarTree = ({ tree, activeId, onSelect }) => {
  return (
    <nav className="tree-nav" aria-label="Documentation tree">
      {tree.map((section) => {
        const sectionIsActive =
          activeId === section.id ||
          section.items.some((item) => item.id === activeId);

        return (
          <div
            className={`tree-group ${sectionIsActive ? 'is-active' : ''}`}
            key={section.id}
          >
            <button
              type="button"
              className={`tree-link tree-link-section ${
                activeId === section.id ? 'is-active' : ''
              }`}
              onClick={() => onSelect(section.id, section.id)}
            >
              {section.label}
            </button>
            {section.items.length > 0 && (
              <div className="tree-subitems">
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`tree-link tree-link-sub ${
                      activeId === item.id ? 'is-active' : ''
                    }`}
                    onClick={() => onSelect(section.id, item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

const SearchModal = ({
  isOpen,
  query,
  onQueryChange,
  onClose,
  results,
  onSelect,
  inputRef,
}) => {
  if (!isOpen) return null;

  return (
    <div className="search-overlay" role="presentation" onClick={onClose}>
      <div
        className="search-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Global documentation search"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="search-modal-header">
          <input
            ref={inputRef}
            className="search-input"
            value={query}
            placeholder="Search sections and items"
            onChange={(event) => onQueryChange(event.target.value)}
          />
          <button type="button" className="search-close" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="search-results">
          {results.length === 0 && (
            <p className="search-empty">No matching items found.</p>
          )}
          {results.map((result) => (
            <button
              key={result.key}
              type="button"
              className="search-result-button"
              onClick={() => onSelect(result.sectionId, result.targetId)}
            >
              <span className="search-result-title">{result.label}</span>
              <span className="search-result-meta">{result.path}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const HomePage = ({
  title,
  description,
  points,
  theme,
  position,
  status,
  message,
  onChangeTitle,
  onChangeDescription,
  onChangePoints,
  onChangeTheme,
  onChangePosition,
  onPopNormal,
  onPopRare,
}) => (
  <section className="docs-section" id="home">
    <h2>Achievement Playground</h2>
    <p>
      Use this landing page to quickly test mock achievements in your app with
      custom title, description, score, color, and popup position.
    </p>

    <h3 id="home-play">Quick Play</h3>
    <div className="landing-actions">
      <button type="button" onClick={onPopNormal}>
        Pop Normal Achievement
      </button>
      <button type="button" className="rare" onClick={onPopRare}>
        Pop Rare Achievement
      </button>
    </div>

    <h3 id="home-options">Display Options</h3>
    <div className="landing-options">
      <div className="landing-option-block">
        <p className="landing-option-label">Popup Position</p>
        <div className="landing-radio-group">
          {LANDING_POSITION_OPTIONS.map((option) => (
            <label key={option.value} className="landing-radio-option">
              <input
                type="radio"
                name="landing-position"
                value={option.value}
                checked={position === option.value}
                onChange={(event) => onChangePosition(event.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="landing-option-block">
        <label className="landing-option-label" htmlFor="landing-theme">
          Color Preset
        </label>
        <select
          id="landing-theme"
          className="landing-select"
          value={theme}
          onChange={(event) => onChangeTheme(event.target.value)}
        >
          {LANDING_THEME_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>

    <h3 id="home-fields">Mock Achievement Fields</h3>
    <div className="landing-fields">
      <label className="landing-field">
        <span>Title</span>
        <input
          type="text"
          value={title}
          onChange={(event) => onChangeTitle(event.target.value)}
          placeholder="Achievement title"
        />
      </label>
      <label className="landing-field">
        <span>Description</span>
        <textarea
          value={description}
          onChange={(event) => onChangeDescription(event.target.value)}
          rows={3}
          placeholder="Achievement description"
        />
      </label>
      <label className="landing-field">
        <span>Point Value</span>
        <input
          type="number"
          min="0"
          step="1"
          value={points}
          onChange={(event) => onChangePoints(event.target.value)}
        />
      </label>
    </div>

    <h3 id="home-actions">Status</h3>
    <div className="landing-status" data-state={status}>
      {message}
    </div>
  </section>
);

const SetupPage = () => (
  <section className="docs-section" id="setup">
    <h2>Setup and Loading</h2>
    <p>
      Add the plugin script and create one <code>AchievementSystem</code> instance
      for your app runtime.
    </p>
    <p>
      Plugin URL: <code>{PLUGIN_URL}</code>. Use{' '}
      <code>type=&quot;module&quot;</code> when loading from a script tag.
    </p>
    <div className="script-grid">
      {setupScripts.map((script) => (
        <div className="script-card" key={script.id}>
          <h3 id={`setup-${script.id}`}>{script.title}</h3>
          <p>{script.description}</p>
          <CodeSnippet
            code={script.code}
            language={script.language}
            title={script.title}
          />
        </div>
      ))}
    </div>
  </section>
);

const ConstructorPage = () => (
  <section className="docs-section" id="constructor">
    <h2>Constructor and Static Presets</h2>
    <p>
      Build one system instance and reuse it across your app. You can modify
      options later with <code>configure()</code>.
    </p>
    <h3 id="constructor-quick-start">Quick Start</h3>
    <CodeSnippet
      code={constructorSnippet}
      language="javascript"
      title="Constructor Example"
    />
    <h3 id="constructor-presets">Color Presets</h3>
    <CodeSnippet
      code={colorPresetSnippet}
      language="javascript"
      title="COLOR_PRESETS Access"
    />
    <h3 id="constructor-options">Options Reference</h3>
    <DataTable
      columns={[
        { key: 'name', label: 'Option' },
        { key: 'type', label: 'Type' },
        { key: 'defaultValue', label: 'Default' },
        { key: 'description', label: 'Description' },
      ]}
      rows={constructorOptions}
    />
  </section>
);

const DefinitionsPage = () => (
  <section className="docs-section" id="definitions">
    <h2>Achievement Definitions and View Shape</h2>
    <p>
      Register definitions once at app startup. Query methods return a merged
      view object containing definition + state.
    </p>
    <h3 id="definitions-schema">Definition Schema</h3>
    <DataTable
      columns={[
        { key: 'name', label: 'Field' },
        { key: 'required', label: 'Required' },
        { key: 'type', label: 'Type' },
        { key: 'defaultValue', label: 'Default' },
        { key: 'description', label: 'Description' },
      ]}
      rows={definitionFields}
    />
    <h3 id="definitions-view">AchievementView Returned by get()/getAll()</h3>
    <DataTable
      columns={[
        { key: 'name', label: 'Field' },
        { key: 'type', label: 'Type' },
        { key: 'description', label: 'Description' },
      ]}
      rows={viewShapeFields}
    />
  </section>
);

const EventsPage = () => (
  <section className="docs-section" id="events">
    <h2>Events</h2>
    <p>
      The class extends an internal event emitter. Subscribe via <code>on()</code>{' '}
      and clean up with <code>off()</code>.
    </p>
    <h3 id="events-hooks">Event Hooks</h3>
    <CodeSnippet code={eventSnippet} language="javascript" title="Event Hooks" />
    <h3 id="events-reference">Event Reference Table</h3>
    <DataTable
      columns={[
        { key: 'name', label: 'Event' },
        { key: 'payload', label: 'Payload' },
        { key: 'description', label: 'Description' },
      ]}
      rows={eventReference}
    />
  </section>
);

const ExamplesPage = ({ onPlayExample }) => (
  <section className="docs-section" id="examples">
    <h2>Playable Examples</h2>
    <p>
      Each example below can run directly in this page. Click <code>Play</code> to
      execute the script against the loaded plugin.
    </p>
    <div className="script-grid">
      {playableExamples.map((example) => (
        <div className="script-card" key={example.id}>
          <h3 id={`example-${example.id}`}>{example.title}</h3>
          <p>{example.description}</p>
          <CodeSnippet
            code={example.code}
            language={example.language}
            title={example.title}
          />
          <button
            type="button"
            className="play-example-button"
            onClick={() => onPlayExample(example)}
          >
            Play
          </button>
        </div>
      ))}
    </div>
  </section>
);

const MethodsPage = ({ allMethods }) => (
  <section className="docs-section" id="methods">
    <h2>Method-by-Method API Reference ({allMethods.length})</h2>
    {methodGroups.map((group) => (
      <div className="method-group" key={group.title}>
        <h3 id={`method-group-${makeId(group.title)}`}>{group.title}</h3>
        <div className="method-grid">
          {group.methods.map((method) => (
            <article className="method-card" key={method.name}>
              <header>
                <h4>{method.name}</h4>
                <code>{method.signature}</code>
              </header>
              <p>{method.description}</p>
              <p className="returns-line">
                <strong>Returns:</strong> <code>{method.returns}</code>
              </p>
              <CodeSnippet
                code={method.code}
                language="javascript"
                title={`${method.name} Example`}
              />
            </article>
          ))}
        </div>
      </div>
    ))}
  </section>
);

const RecipesPage = () => (
  <section className="docs-section" id="recipes">
    <h2>Copy-Ready Usage Scripts</h2>
    <div className="script-grid">
      {scriptRecipes.map((script) => (
        <div className="script-card" key={script.id}>
          <h3 id={`recipe-${script.id}`}>{script.title}</h3>
          <p>{script.description}</p>
          <CodeSnippet
            code={script.code}
            language={script.language}
            title={script.title}
          />
        </div>
      ))}
    </div>
  </section>
);

const PlaygroundPage = ({ demoStatus, demoMessage, demoStats, demoOutput, onRun, onAddProgress }) => (
  <section className="docs-section" id="playground">
    <h2>Live Plugin Playground</h2>
    <p>
      These controls load the production plugin URL, instantiate a local demo
      system, and execute real API calls.
    </p>

    <h3 id="playground-controls">Controls</h3>
    <div className="playground-status" data-state={demoStatus}>
      <span className="status-dot" />
      <span>{demoMessage}</span>
    </div>

    <div className="playground-controls">
      <button type="button" onClick={() => onRun('Plugin loaded and demo initialized.', () => {})}>
        Initialize Plugin
      </button>
      <button
        type="button"
        onClick={() =>
          onRun('Common unlock triggered.', (system) => {
            system.revoke('demo-common').unlock('demo-common');
            return system.get('demo-common', { revealSecrets: true });
          })
        }
      >
        Trigger Common Unlock
      </button>
      <button
        type="button"
        onClick={() =>
          onRun('Rare unlock triggered.', (system) => {
            system.revoke('demo-rare').unlock('demo-rare');
            return system.get('demo-rare', { revealSecrets: true });
          })
        }
      >
        Trigger Rare Unlock
      </button>
      <button type="button" onClick={() => onRun('Progress +10 applied.', (system) => onAddProgress(system, 10))}>
        Add Progress (+10)
      </button>
      <button
        type="button"
        onClick={() =>
          onRun('Manual notify() triggered.', (system) => {
            system.notify('demo-common');
            return 'Notification queued for demo-common.';
          })
        }
      >
        Notify Existing Achievement
      </button>
      <button
        type="button"
        onClick={() =>
          onRun('Notification queue cleared.', (system) => {
            system.clearNotificationQueue();
            return 'Pending notifications removed.';
          })
        }
      >
        Clear Notification Queue
      </button>
      <button
        type="button"
        className="danger"
        onClick={() =>
          onRun('Demo state reset.', (system) => {
            system.reset();
            return system.getStats();
          })
        }
      >
        Reset Demo State
      </button>
    </div>

    <h3 id="playground-stats">Output and Stats</h3>
    {demoOutput && <pre className="demo-output">{demoOutput}</pre>}

    {demoStats && (
      <div className="demo-stats">
        <div>
          <span>Total</span>
          <strong>{demoStats.total}</strong>
        </div>
        <div>
          <span>Unlocked</span>
          <strong>{demoStats.unlocked}</strong>
        </div>
        <div>
          <span>Locked</span>
          <strong>{demoStats.locked}</strong>
        </div>
        <div>
          <span>Completion</span>
          <strong>{demoStats.percentage}%</strong>
        </div>
        <div>
          <span>Score</span>
          <strong>
            {demoStats.score.earned}/{demoStats.score.total}
          </strong>
        </div>
      </div>
    )}
  </section>
);

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [landingTitle, setLandingTitle] = useState('Preview Achievement');
  const [landingDescription, setLandingDescription] = useState(
    'Created from the landing playground.',
  );
  const [landingPoints, setLandingPoints] = useState('25');
  const [landingTheme, setLandingTheme] = useState('xboxGreen');
  const [landingPosition, setLandingPosition] = useState('bottom-right');
  const [landingStatus, setLandingStatus] = useState('idle');
  const [landingMessage, setLandingMessage] = useState(
    'Set your fields, then pop a normal or rare achievement.',
  );

  const [demoStatus, setDemoStatus] = useState('idle');
  const [demoMessage, setDemoMessage] = useState(
    'Load the plugin, then trigger actions below.',
  );
  const [demoStats, setDemoStats] = useState(null);
  const [demoOutput, setDemoOutput] = useState('');

  const mainContentRef = useRef(null);
  const searchInputRef = useRef(null);
  const landingRef = useRef(null);
  const demoRef = useRef(null);

  const allMethods = useMemo(
    () =>
      methodGroups.flatMap((group) =>
        group.methods.map((method) => ({ ...method, group: group.title })),
      ),
    [],
  );

  const navTree = useMemo(
    () => [
      {
        id: 'home',
        path: '/home',
        label: 'Home',
        items: [
          { id: 'home-play', label: 'Quick Play' },
          { id: 'home-options', label: 'Display Options' },
          { id: 'home-fields', label: 'Mock Fields' },
          { id: 'home-actions', label: 'Status' },
        ],
      },
      {
        id: 'setup',
        path: '/setup',
        label: 'Setup',
        items: setupScripts.map((script) => ({
          id: `setup-${script.id}`,
          label: script.title,
        })),
      },
      {
        id: 'constructor',
        path: '/constructor',
        label: 'Constructor',
        items: [
          { id: 'constructor-quick-start', label: 'Quick Start' },
          { id: 'constructor-presets', label: 'Color Presets' },
          { id: 'constructor-options', label: 'Options Reference' },
        ],
      },
      {
        id: 'definitions',
        path: '/definitions',
        label: 'Definitions',
        items: [
          { id: 'definitions-schema', label: 'Definition Schema' },
          { id: 'definitions-view', label: 'Achievement View Shape' },
        ],
      },
      {
        id: 'events',
        path: '/events',
        label: 'Events',
        items: [
          { id: 'events-hooks', label: 'Event Hooks' },
          { id: 'events-reference', label: 'Event Reference Table' },
        ],
      },
      {
        id: 'examples',
        path: '/examples',
        label: 'Playable Examples',
        items: playableExamples.map((example) => ({
          id: `example-${example.id}`,
          label: example.title,
        })),
      },
      {
        id: 'methods',
        path: '/methods',
        label: 'Methods',
        items: methodGroups.map((group) => ({
          id: `method-group-${makeId(group.title)}`,
          label: group.title,
        })),
      },
      {
        id: 'recipes',
        path: '/recipes',
        label: 'Recipes',
        items: scriptRecipes.map((script) => ({
          id: `recipe-${script.id}`,
          label: script.title,
        })),
      },
      {
        id: 'playground',
        path: '/playground',
        label: 'Playground',
        items: [
          { id: 'playground-controls', label: 'Controls' },
          { id: 'playground-stats', label: 'Output and Stats' },
        ],
      },
    ],
    [],
  );

  const sectionById = useMemo(
    () => Object.fromEntries(navTree.map((section) => [section.id, section])),
    [navTree],
  );

  const sectionByPath = useMemo(
    () => Object.fromEntries(navTree.map((section) => [section.path, section])),
    [navTree],
  );

  const currentSection = useMemo(() => {
    const path = normalizePath(location.pathname);
    return sectionByPath[path] || sectionByPath[DEFAULT_ROUTE_PATH];
  }, [location.pathname, sectionByPath]);

  const activeNavId = useMemo(() => {
    const hashTarget = decodeURIComponent((location.hash || '').replace('#', ''));
    if (!hashTarget) return currentSection.id;

    const isKnownItem = currentSection.items.some((item) => item.id === hashTarget);
    return isKnownItem ? hashTarget : currentSection.id;
  }, [currentSection, location.hash]);

  const totalSections = navTree.length;
  const totalScripts =
    scriptRecipes.length + setupScripts.length + playableExamples.length;

  const sectionSearchText = useMemo(
    () => ({
      home: [
        'Achievement Playground',
        'Use this landing page to quickly test mock achievements in your app.',
        'normal achievement',
        'rare achievement',
        'popup position',
        'color preset',
        'title',
        'description',
        'point value',
      ].join(' '),
      setup: [
        'Setup and Loading',
        'Add the plugin script and create one AchievementSystem instance for your app runtime.',
        'Plugin URL',
        PLUGIN_URL,
        ...setupScripts.flatMap((script) => [
          script.title,
          script.description,
          script.code,
        ]),
      ].join(' '),
      constructor: [
        'Constructor and Static Presets',
        'Build one system instance and reuse it across your app.',
        'modify options later with configure',
        constructorSnippet,
        colorPresetSnippet,
        ...constructorOptions.flatMap((option) => [
          option.name,
          option.type,
          option.defaultValue,
          option.description,
        ]),
      ].join(' '),
      definitions: [
        'Achievement Definitions and View Shape',
        'Definition Schema',
        'AchievementView Returned by get and getAll',
        ...definitionFields.flatMap((field) => [
          field.name,
          field.required,
          field.type,
          field.defaultValue,
          field.description,
        ]),
        ...viewShapeFields.flatMap((field) => [
          field.name,
          field.type,
          field.description,
        ]),
      ].join(' '),
      events: [
        'Events',
        'Event hooks and event reference table',
        eventSnippet,
        ...eventReference.flatMap((event) => [
          event.name,
          event.payload,
          event.description,
        ]),
      ].join(' '),
      examples: [
        'Playable examples',
        'Click play to execute the script against the loaded plugin',
        ...playableExamples.flatMap((example) => [
          example.title,
          example.description,
          example.code,
        ]),
      ].join(' '),
      methods: [
        'Method-by-method API reference',
        ...methodGroups.flatMap((group) => [
          group.title,
          ...group.methods.flatMap((method) => [
            method.name,
            method.signature,
            method.returns,
            method.description,
            method.code,
          ]),
        ]),
      ].join(' '),
      recipes: [
        'Copy-ready usage scripts',
        ...scriptRecipes.flatMap((script) => [
          script.title,
          script.description,
          script.code,
        ]),
      ].join(' '),
      playground: [
        'Live plugin playground',
        'Initialize Plugin',
        'Trigger Common Unlock',
        'Trigger Rare Unlock',
        'Add Progress',
        'Notify Existing Achievement',
        'Clear Notification Queue',
        'Reset Demo State',
        'Output and Stats',
      ].join(' '),
    }),
    [],
  );

  const itemSearchText = useMemo(() => {
    const index = {};

    index['home-play'] = [
      'Quick Play',
      'Pop Normal Achievement',
      'Pop Rare Achievement',
    ].join(' ');
    index['home-options'] = [
      'Display Options',
      'Popup Position',
      ...LANDING_POSITION_OPTIONS.map((option) => option.label),
      'Color Preset',
      ...LANDING_THEME_OPTIONS.map((option) => option.label),
    ].join(' ');
    index['home-fields'] = [
      'Mock Achievement Fields',
      'Title',
      'Description',
      'Point Value',
    ].join(' ');
    index['home-actions'] = ['Status', 'landing status message'].join(' ');

    for (const script of setupScripts) {
      index[`setup-${script.id}`] = [
        script.title,
        script.description,
        script.code,
      ].join(' ');
    }

    index['constructor-quick-start'] = [
      'Quick Start',
      'Build one system instance and reuse it across your app',
      constructorSnippet,
    ].join(' ');

    index['constructor-presets'] = [
      'Color Presets',
      colorPresetSnippet,
      'COLOR_PRESETS',
    ].join(' ');

    index['constructor-options'] = [
      'Options Reference',
      ...constructorOptions.flatMap((option) => [
        option.name,
        option.type,
        option.defaultValue,
        option.description,
      ]),
    ].join(' ');

    index['definitions-schema'] = [
      'Definition Schema',
      ...definitionFields.flatMap((field) => [
        field.name,
        field.required,
        field.type,
        field.defaultValue,
        field.description,
      ]),
    ].join(' ');

    index['definitions-view'] = [
      'AchievementView Returned by get and getAll',
      ...viewShapeFields.flatMap((field) => [
        field.name,
        field.type,
        field.description,
      ]),
    ].join(' ');

    index['events-hooks'] = ['Event Hooks', eventSnippet].join(' ');
    index['events-reference'] = [
      'Event Reference Table',
      ...eventReference.flatMap((event) => [
        event.name,
        event.payload,
        event.description,
      ]),
    ].join(' ');

    for (const example of playableExamples) {
      index[`example-${example.id}`] = [
        example.title,
        example.description,
        example.code,
      ].join(' ');
    }

    for (const group of methodGroups) {
      index[`method-group-${makeId(group.title)}`] = [
        group.title,
        ...group.methods.flatMap((method) => [
          method.name,
          method.signature,
          method.returns,
          method.description,
          method.code,
        ]),
      ].join(' ');
    }

    for (const script of scriptRecipes) {
      index[`recipe-${script.id}`] = [
        script.title,
        script.description,
        script.code,
      ].join(' ');
    }

    index['playground-controls'] = [
      'Controls',
      'Initialize Plugin',
      'Trigger Common Unlock',
      'Trigger Rare Unlock',
      'Add Progress',
      'Notify Existing Achievement',
      'Clear Notification Queue',
      'Reset Demo State',
    ].join(' ');

    index['playground-stats'] = [
      'Output and Stats',
      'total unlocked locked completion score',
      'demo output',
    ].join(' ');

    return index;
  }, []);

  const searchableItems = useMemo(() => {
    return navTree.flatMap((section) => {
      const sectionNode = {
        key: `section-${section.id}`,
        sectionId: section.id,
        targetId: section.id,
        label: section.label,
        path: section.label,
        searchIndex: `${section.label} ${sectionSearchText[section.id] || ''}`
          .toLowerCase(),
      };

      const childNodes = section.items.map((item) => ({
        key: `item-${section.id}-${item.id}`,
        sectionId: section.id,
        targetId: item.id,
        label: item.label,
        path: `${section.label} / ${item.label}`,
        searchIndex: `${section.label} ${item.label} ${
          itemSearchText[item.id] || sectionSearchText[section.id] || ''
        }`.toLowerCase(),
      }));

      return [sectionNode, ...childNodes];
    });
  }, [itemSearchText, navTree, sectionSearchText]);

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return searchableItems.slice(0, 60);
    }

    return searchableItems
      .filter((item) => item.searchIndex.includes(query))
      .slice(0, 60);
  }, [searchQuery, searchableItems]);

  const ensureLandingSystem = useCallback(async () => {
    const AchievementSystem = await loadAchievementPlugin();

    if (!landingRef.current) {
      landingRef.current = new AchievementSystem({
        theme: landingTheme,
        position: landingPosition,
        persistence: false,
        autoSave: false,
        debug: false,
      });
    }

    landingRef.current.setTheme(landingTheme);
    landingRef.current.configure({ position: landingPosition });
    return landingRef.current;
  }, [landingPosition, landingTheme]);

  const popLandingAchievement = useCallback(
    async (rare) => {
      setLandingStatus('loading');

      try {
        const system = await ensureLandingSystem();
        const id = `landing-preview-${Date.now()}-${Math.random()
          .toString(16)
          .slice(2, 8)}`;

        const parsedPoints = Number.parseInt(landingPoints, 10);
        const score = Number.isFinite(parsedPoints) && parsedPoints > 0 ? parsedPoints : 0;
        const title = landingTitle.trim() || (rare ? 'Rare Preview' : 'Preview');
        const description = landingDescription.trim();

        system.register({
          id,
          title,
          description,
          score,
          rare,
          category: 'landing-playground',
        });

        system.unlock(id);
        setTimeout(() => system.unregister(id), 100);

        setLandingStatus('ready');
        setLandingMessage(`${rare ? 'Rare' : 'Normal'} achievement popped.`);
      } catch (error) {
        setLandingStatus('error');
        setLandingMessage(error.message);
      }
    },
    [ensureLandingSystem, landingDescription, landingPoints, landingTitle],
  );

  const ensureDemoSystem = useCallback(async () => {
    const AchievementSystem = await loadAchievementPlugin();

    if (!demoRef.current) {
      const instance = new AchievementSystem({
        theme: 'xboxGreen',
        position: 'bottom-right',
        persistence: false,
        debug: false,
      });

      instance.registerMany(DEMO_DEFINITIONS);
      demoRef.current = instance;
      setDemoMessage('Demo initialized. Use any action to test the API.');
    }

    return demoRef.current;
  }, []);

  const getMarathonView = useCallback((system) => {
    return system.get('demo-marathon', { revealSecrets: true });
  }, []);

  const addMarathonProgress = useCallback(
    (system, amount) => {
      const current = getMarathonView(system);
      if (!current) return null;

      if (current.unlocked) {
        system.revoke('demo-marathon');
      }

      const refreshed = getMarathonView(system);
      if (refreshed && refreshed.progress >= refreshed.maxProgress) {
        system.setProgress('demo-marathon', 0);
      }

      system.progress('demo-marathon', amount);
      return getMarathonView(system);
    },
    [getMarathonView],
  );

  const playActions = useMemo(
    () => ({
      init: () => 'AchievementSystem initialized.',
      commonUnlock: (system) => {
        system.revoke('demo-common');
        system.unlock('demo-common');
        return system.get('demo-common', { revealSecrets: true });
      },
      rareUnlock: (system) => {
        system.revoke('demo-rare');
        system.unlock('demo-rare');
        return system.get('demo-rare', { revealSecrets: true });
      },
      addProgress10: (system) => addMarathonProgress(system, 10),
      completeProgress: (system) => {
        system.revoke('demo-marathon');
        system.setProgress('demo-marathon', 100);
        return system.get('demo-marathon', { revealSecrets: true });
      },
      notifyCommon: (system) => {
        system.notify('demo-common');
        return 'Notification queued for demo-common.';
      },
      setThemeBlue: (system) => {
        system.setTheme('brightBlue');
        system.notify('demo-common');
        return 'Theme changed to brightBlue.';
      },
      positionTopRight: (system) => {
        system.configure({ position: 'top-right' });
        system.notify('demo-common');
        return 'Notification position set to top-right.';
      },
      mute: (system) => {
        system.mute();
        return 'Sound muted.';
      },
      unmute: (system) => {
        system.unmute();
        return 'Sound unmuted.';
      },
      stats: (system) => system.getStats(),
      reset: (system) => {
        system.reset();
        return system.getStats();
      },
    }),
    [addMarathonProgress],
  );

  const runDemoAction = useCallback(
    async (actionLabel, action) => {
      setDemoStatus('loading');

      try {
        const system = await ensureDemoSystem();
        const result = action(system);
        setDemoStats(system.getStats());

        if (result !== undefined) {
          setDemoOutput(
            typeof result === 'string' ? result : JSON.stringify(result, null, 2),
          );
        }

        setDemoStatus('ready');
        setDemoMessage(actionLabel);
      } catch (error) {
        setDemoStatus('error');
        setDemoMessage(error.message);
      }
    },
    [ensureDemoSystem],
  );

  const handlePlayExample = useCallback(
    (example) => {
      const action = playActions[example.actionKey];
      if (!action) {
        runDemoAction('No demo action is mapped for this example yet.', () => {});
        return;
      }
      runDemoAction(`Played: ${example.title}`, action);
    },
    [playActions, runDemoAction],
  );

  const handleSelectNav = useCallback(
    (sectionId, targetId = sectionId) => {
      const section = sectionById[sectionId] || sectionByPath[DEFAULT_ROUTE_PATH];
      const nextHash = targetId !== section.id ? `#${targetId}` : '';
      navigate(`${section.path}${nextHash}`);
      setIsSearchOpen(false);
    },
    [navigate, sectionById, sectionByPath],
  );

  useEffect(() => {
    return () => {
      if (landingRef.current) {
        landingRef.current.destroy();
        landingRef.current = null;
      }
      if (demoRef.current) {
        demoRef.current.destroy();
        demoRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isSearchOpen) return;
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsSearchOpen(true);
      }

      if (event.key === 'Escape') {
        setIsSearchOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const container = mainContentRef.current;
    if (!container) return;

    container.scrollTo({ top: 0, behavior: 'auto' });

    const hashTarget = decodeURIComponent((location.hash || '').replace('#', ''));
    if (!hashTarget) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(hashTarget);
      if (!target) return;

      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const nextTop = targetRect.top - containerRect.top + container.scrollTop - 8;

      container.scrollTo({ top: Math.max(nextTop, 0), behavior: 'smooth' });
    });

    return () => cancelAnimationFrame(frame);
  }, [location.pathname, location.hash]);

  return (
    <div className="docs-app">
      <header className="hero" id="top">
        <p className="eyebrow">AchievementSystem v2</p>
        <h1>
          <Link className="hero-title-link" to={DEFAULT_ROUTE_PATH}>
            Achievement Plugin Reference
          </Link>
        </h1>
        <p className="hero-copy">
          Add Xbox-style achievements to your app with unlock notifications,
          progress tracking, persistence, and events.
        </p>
        <p className="hero-meta">
          {methodCount} methods · {totalScripts} scripts · {totalSections} sections
        </p>
      </header>

      <div className="docs-shell">
        <aside className="docs-sidebar">
          <div className="docs-sidebar-inner">
            <div className="docs-sidebar-tools">
              <p className="tree-title">Page Index</p>
              <button
                type="button"
                className="search-open-button"
                onClick={() => setIsSearchOpen(true)}
              >
                Search
              </button>
            </div>
            <SidebarTree
              tree={navTree}
              activeId={activeNavId}
              onSelect={handleSelectNav}
            />
          </div>
        </aside>

        <main className="docs-main" ref={mainContentRef}>
          <Routes>
            <Route path="/" element={<Navigate to={DEFAULT_ROUTE_PATH} replace />} />
            <Route
              path="/home"
              element={
                <HomePage
                  title={landingTitle}
                  description={landingDescription}
                  points={landingPoints}
                  theme={landingTheme}
                  position={landingPosition}
                  status={landingStatus}
                  message={landingMessage}
                  onChangeTitle={setLandingTitle}
                  onChangeDescription={setLandingDescription}
                  onChangePoints={setLandingPoints}
                  onChangeTheme={setLandingTheme}
                  onChangePosition={setLandingPosition}
                  onPopNormal={() => popLandingAchievement(false)}
                  onPopRare={() => popLandingAchievement(true)}
                />
              }
            />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/constructor" element={<ConstructorPage />} />
            <Route path="/definitions" element={<DefinitionsPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route
              path="/examples"
              element={<ExamplesPage onPlayExample={handlePlayExample} />}
            />
            <Route path="/methods" element={<MethodsPage allMethods={allMethods} />} />
            <Route path="/recipes" element={<RecipesPage />} />
            <Route
              path="/playground"
              element={
                <PlaygroundPage
                  demoStatus={demoStatus}
                  demoMessage={demoMessage}
                  demoStats={demoStats}
                  demoOutput={demoOutput}
                  onRun={runDemoAction}
                  onAddProgress={addMarathonProgress}
                />
              }
            />
            <Route path="*" element={<Navigate to={DEFAULT_ROUTE_PATH} replace />} />
          </Routes>
        </main>
      </div>

      <SearchModal
        isOpen={isSearchOpen}
        query={searchQuery}
        onQueryChange={setSearchQuery}
        onClose={() => setIsSearchOpen(false)}
        results={searchResults}
        onSelect={handleSelectNav}
        inputRef={searchInputRef}
      />
    </div>
  );
}

export default App;
